import React from "react";
import { motion } from "framer-motion";
import {
  FaFacebookF, FaInstagram, FaTwitter,
  FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaWhatsapp,
} from "react-icons/fa";

const contactCards = [
  {
    icon: <FaPhoneAlt />,
    label: "Call Us",
    color: "from-blue-600 to-blue-800",
    iconBg: "bg-blue-100 text-blue-700",
    items: [
      { text: "01709919825", href: "tel:+8801709919825" },
      { text: "01709919827", href: "tel:+8801709919827" },
      { text: "01335185110", href: "tel:+8801335185110" },
      { text: "01709919829", href: "tel:+8801709919829" },
      { text: "Investment: 01711074278", href: "tel:+8801711074278" },
    ],
  },
  {
    icon: <FaEnvelope />,
    label: "Email Us",
    color: "from-emerald-600 to-teal-700",
    iconBg: "bg-emerald-100 text-emerald-700",
    items: [
      { text: "booking@chutipurbachal.com", sub: "Booking", href: "mailto:booking@chutipurbachal.com" },
      { text: "suitesale@chutipurbachal.com", sub: "Investment", href: "mailto:suitesale@chutipurbachal.com" },
    ],
  },
  {
    icon: <FaMapMarkerAlt />,
    label: "Our Location",
    color: "from-violet-600 to-purple-800",
    iconBg: "bg-violet-100 text-violet-700",
    items: [
      {
        text: "Chuti Resort Purbachal, Galan, Rathura, Purbachal New Town, Gazipur, Bangladesh",
        href: "https://www.google.com/maps/search/Chuti+Resort+Purbachal+Galan+Rathura+Purbachal+New+Town+Gazipur",
        external: true,
      },
    ],
  },
];

const socialLinks = [
  { name: "Facebook", url: "https://www.facebook.com/chutipurbachal", icon: <FaFacebookF />, color: "bg-blue-600 hover:bg-blue-700" },
  { name: "Instagram", url: "https://www.instagram.com/chutiresortpurbachal?fbclid=IwY2xjawQht_FleHRuA2FlbQIxMABicmlkETF6YjYyb3EwMjZVeXBVRDAyc3J0YwZhcHBfaWQQMjIyMDM5MTc4ODIwMDg5MgABHvq84A50m2OGWM8HFqZBxUtuDzUU19gmHKvl-dFsucO_a79Y4P0qyCvvFsMM_aem_8ZYxRTXbgoWsWZuU2bSWzA", icon: <FaInstagram />, color: "bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600" },
  { name: "WhatsApp", url: "https://wa.me/8801709919827", icon: <FaWhatsapp />, color: "bg-green-500 hover:bg-green-600" },
  { name: "Twitter / X", url: "https://x.com/chutiresortpurbachal", icon: <FaTwitter />, color: "bg-black hover:bg-gray-800" },
];

const whyUs = [
  "Conveniently located in Purbachal, near Dhaka",
  "Luxury accommodations with modern amenities",
  "Beautiful natural surroundings and serene atmosphere",
  "Wide range of recreational and event facilities",
];

function Contact() {
  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Hero ── */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-10 px-4 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-blue-300 uppercase tracking-[0.25em] text-xs font-semibold mb-2">Chuti Resort Purbachal</p>
          <h1 className="text-3xl md:text-5xl font-fancy font-bold drop-shadow mb-2">Get In Touch</h1>
          <p className="text-blue-200 text-sm md:text-base max-w-xl mx-auto">
            We&apos;d love to hear from you — reservations, enquiries, or just a hello.
          </p>
        </motion.div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">

        {/* ── Contact Cards ── */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 gap-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
        >
          {contactCards.map((card) => (
            <motion.div
              key={card.label}
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
              className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              {/* Card top accent */}
              <div className={`h-1.5 bg-gradient-to-r ${card.color}`} />
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0 ${card.iconBg}`}>
                    {card.icon}
                  </span>
                  <h3 className="font-bold text-gray-800 font-fancy text-base">{card.label}</h3>
                </div>
                <ul className="space-y-1.5">
                  {card.items.map((item, i) => (
                    <li key={i}>
                      <a
                        href={item.href}
                        target={item.external ? "_blank" : undefined}
                        rel={item.external ? "noopener noreferrer" : undefined}
                        className="flex items-start gap-1.5 text-sm text-gray-600 hover:text-blue-600 transition-colors group"
                      >
                        <span className="leading-snug">
                          {item.sub && <span className="text-gray-400 text-xs block">{item.sub}</span>}
                          {item.text}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* ── Main Grid: Form + Map/Social ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Form ── */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="bg-white rounded-2xl shadow-md border border-gray-100 p-6"
          >
            <h2 className="text-xl font-bold text-gray-800 mb-5 font-fancy">Send Us a Message</h2>
            <form className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Your Name</label>
                  <input type="text" placeholder="Full name" className="w-full px-4 py-2.5 bg-slate-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-sm text-gray-800" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone Number</label>
                  <input type="tel" placeholder="Mobile number" className="w-full px-4 py-2.5 bg-slate-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-sm text-gray-800" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email Address</label>
                <input type="email" placeholder="your@email.com" className="w-full px-4 py-2.5 bg-slate-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-sm text-gray-800" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Service</label>
                <select className="w-full px-4 py-2.5 bg-slate-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-sm text-gray-800">
                  <option value="">Select a service</option>
                  <option value="room-booking">Room Booking</option>
                  <option value="villa-booking">Villa / Cottage Booking</option>
                  <option value="event-inquiry">Event Inquiry</option>
                  <option value="conference">Conference / Meeting</option>
                  <option value="general-inquiry">General Inquiry</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Message</label>
                <textarea rows={4} placeholder="How can we help you?" className="w-full px-4 py-2.5 bg-slate-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-sm text-gray-800 resize-none" />
              </div>
              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950 text-white font-bold py-3 rounded-xl shadow transition-all duration-300 tracking-wide text-sm"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                SEND MESSAGE
              </motion.button>
            </form>
          </motion.div>

          {/* ── Right column ── */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="flex flex-col gap-5"
          >
            {/* Map */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden flex-shrink-0">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14593.019407508606!2d90.505132!3d23.88058!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755d35b4d2d86eb%3A0x2e2a0bdde7fe10c0!2sChuti%20Resort%20Purbachal!5e0!3m2!1sen!2sbd!4v1773386915534!5m2!1sen!2sbd"
                width="100%"
                height="220"
                style={{ border: 0, display: "block" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Chuti Resort Purbachal Location"
              />
            </div>

            {/* Social + Why Choose in a side-by-side grid on md+ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">

              {/* Social */}
              <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 flex flex-col">
                <h3 className="font-bold text-gray-800 font-fancy text-base mb-1">Connect With Us</h3>
                <p className="text-gray-500 text-xs mb-4">Follow for updates & special offers</p>
                <div className="flex flex-wrap gap-3 mt-auto">
                  {socialLinks.map((s) => (
                    <motion.a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={s.name}
                      className={`w-10 h-10 ${s.color} text-white rounded-xl flex items-center justify-center text-base shadow transition-all duration-200`}
                      whileHover={{ scale: 1.12, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {s.icon}
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* Why Choose */}
              <div className="bg-gradient-to-br from-blue-800 to-indigo-900 rounded-2xl shadow-md p-5 text-white flex flex-col">
                <h3 className="font-bold font-fancy text-base mb-3">Why Choose Us?</h3>
                <ul className="space-y-2 mt-auto">
                  {whyUs.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-blue-100 leading-snug">
                      <svg className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
